import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { inspectionAPI, vehicleAPI } from '../lib/api';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
    Search, ClipboardCheck, Loader2, Filter, Calendar, Gauge, 
    Camera, Check, X, AlertTriangle, Eye, User, Truck, FileWarning, Send
} from 'lucide-react';
import { toast } from 'sonner';

const PHOTO_LABELS = {
    front: 'Front View',
    back: 'Rear View',
    left: 'Left Side',
    right: 'Right Side',
    dashboard: 'Dashboard',
    cargo: 'Cargo Area'
};

const PHOTO_LABELS_EL = {
    front: 'Εμπρός Όψη',
    back: 'Πίσω Όψη',
    left: 'Αριστερή Πλευρά',
    right: 'Δεξιά Πλευρά',
    dashboard: 'Ταμπλό',
    cargo: 'Χώρος Φορτίου'
};

const StatusBadge = ({ status }) => {
    const styles = {
        pending: 'badge-warning',
        approved: 'badge-success',
        flagged: 'badge-danger'
    };
    return (
        <span className={`badge ${styles[status] || 'badge-default'}`}>
            {status}
        </span>
    );
};

const PhotoGrid = ({ inspection, onClose }) => {
    const [loadedPhotos, setLoadedPhotos] = useState({});
    const [loadingPhotos, setLoadingPhotos] = useState({});

    const loadPhoto = async (photoType) => {
        if (loadedPhotos[photoType] || loadingPhotos[photoType]) return;
        
        setLoadingPhotos(prev => ({ ...prev, [photoType]: true }));
        try {
            const url = inspectionAPI.getPhotoUrl(inspection.id, photoType);
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('fleet_token')}`
                }
            });
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            setLoadedPhotos(prev => ({ ...prev, [photoType]: objectUrl }));
        } catch (error) {
            console.error(`Failed to load ${photoType} photo:`, error);
        } finally {
            setLoadingPhotos(prev => ({ ...prev, [photoType]: false }));
        }
    };

    useEffect(() => {
        // Load all photos on mount
        inspection.photos?.forEach(p => loadPhoto(p.photo_type));
    }, [inspection.id]);

    return (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {Object.keys(PHOTO_LABELS).map(photoType => {
                const hasPhoto = inspection.photos?.some(p => p.photo_type === photoType);
                return (
                    <div key={photoType} className="space-y-2">
                        <p className="text-xs text-zinc-500 uppercase tracking-wider">{PHOTO_LABELS[photoType]}</p>
                        <div className="aspect-video bg-zinc-800 rounded-sm flex items-center justify-center overflow-hidden">
                            {hasPhoto ? (
                                loadedPhotos[photoType] ? (
                                    <img 
                                        src={loadedPhotos[photoType]} 
                                        alt={PHOTO_LABELS[photoType]}
                                        className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                                        onClick={() => window.open(loadedPhotos[photoType], '_blank')}
                                    />
                                ) : (
                                    <Loader2 className="w-6 h-6 text-zinc-500 animate-spin" />
                                )
                            ) : (
                                <div className="text-center text-zinc-600">
                                    <Camera className="w-8 h-8 mx-auto mb-1" />
                                    <p className="text-xs">Not uploaded</p>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default function InspectionsPage() {
    const { canManage, user } = useAuth();
    const { language } = useLanguage();
    const [inspections, setInspections] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterDate, setFilterDate] = useState('');
    const [selectedInspection, setSelectedInspection] = useState(null);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    
    // Issue Report state
    const [issueReportOpen, setIssueReportOpen] = useState(false);
    const [issueReport, setIssueReport] = useState({
        photo_type: 'all',
        severity: 'medium',
        description: ''
    });
    const [submittingIssue, setSubmittingIssue] = useState(false);

    const t = {
        inspectionDetails: language === 'el' ? 'Λεπτομέρειες Επιθεώρησης' : 'Inspection Details',
        date: language === 'el' ? 'Ημερομηνία' : 'Date',
        driver: language === 'el' ? 'Οδηγός' : 'Driver',
        vehicle: language === 'el' ? 'Όχημα' : 'Vehicle',
        odometer: language === 'el' ? 'Χιλιόμετρα' : 'Odometer',
        notes: language === 'el' ? 'Σημειώσεις' : 'Notes',
        inspectionPhotos: language === 'el' ? 'Φωτογραφίες Επιθεώρησης' : 'Inspection Photos',
        flagIssue: language === 'el' ? 'Σημείωση Προβλήματος' : 'Flag Issue',
        approve: language === 'el' ? 'Έγκριση' : 'Approve',
        reportIssue: language === 'el' ? 'Αναφορά Ζητήματος' : 'Report Issue',
        issueReport: language === 'el' ? 'Αναφορά Ζητήματος' : 'Issue Report',
        selectPhoto: language === 'el' ? 'Επιλέξτε Φωτογραφία' : 'Select Photo',
        severity: language === 'el' ? 'Σοβαρότητα' : 'Severity',
        low: language === 'el' ? 'Χαμηλή' : 'Low',
        medium: language === 'el' ? 'Μέτρια' : 'Medium',
        high: language === 'el' ? 'Υψηλή' : 'High',
        description: language === 'el' ? 'Περιγραφή Ζητήματος' : 'Issue Description',
        descriptionPlaceholder: language === 'el' ? 'Περιγράψτε το πρόβλημα που εντοπίσατε στη φωτογραφία...' : 'Describe the issue you noticed in the photo...',
        cancel: language === 'el' ? 'Ακύρωση' : 'Cancel',
        submit: language === 'el' ? 'Υποβολή' : 'Submit',
        allPhotos: language === 'el' ? 'Όλες οι Φωτογραφίες' : 'All Photos',
        issueReported: language === 'el' ? 'Το ζήτημα αναφέρθηκε επιτυχώς' : 'Issue reported successfully',
        view: language === 'el' ? 'Προβολή' : 'View',
        completed: language === 'el' ? 'Ολοκληρωμένο' : 'Completed'
    };

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [inspectionsRes, vehiclesRes] = await Promise.all([
                inspectionAPI.getAll(),
                vehicleAPI.getAll()
            ]);
            setInspections(inspectionsRes.data);
            setVehicles(vehiclesRes.data);
        } catch (error) {
            toast.error('Failed to fetch inspections');
        } finally {
            setLoading(false);
        }
    };

    const handleViewInspection = (inspection) => {
        setSelectedInspection(inspection);
        setViewDialogOpen(true);
    };

    const handleUpdateStatus = async (inspectionId, status) => {
        try {
            await inspectionAPI.updateStatus(inspectionId, status);
            toast.success(`Inspection ${status}`);
            fetchData();
            setViewDialogOpen(false);
        } catch (error) {
            toast.error('Failed to update status');
        }
    };

    const handleOpenIssueReport = () => {
        setIssueReport({
            photo_type: 'all',
            severity: 'medium',
            description: ''
        });
        setIssueReportOpen(true);
    };

    const handleSubmitIssueReport = async () => {
        if (!issueReport.description.trim()) {
            toast.error(language === 'el' ? 'Παρακαλώ περιγράψτε το ζήτημα' : 'Please describe the issue');
            return;
        }

        setSubmittingIssue(true);
        try {
            await api.post('/inspection-issues', {
                inspection_id: selectedInspection.id,
                vehicle_id: selectedInspection.vehicle_id,
                driver_id: selectedInspection.driver_id,
                photo_type: issueReport.photo_type === 'all' ? null : issueReport.photo_type,
                severity: issueReport.severity,
                description: issueReport.description
            });
            
            toast.success(t.issueReported);
            setIssueReportOpen(false);
            
            // Also flag the inspection
            await inspectionAPI.updateStatus(selectedInspection.id, 'flagged');
            fetchData();
        } catch (error) {
            console.error('Failed to submit issue:', error);
            toast.error(language === 'el' ? 'Αποτυχία υποβολής αναφοράς' : 'Failed to submit report');
        } finally {
            setSubmittingIssue(false);
        }
    };

    const filteredInspections = inspections.filter(inspection => {
        const matchesSearch = 
            inspection.driver_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            inspection.vehicle_info?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = filterStatus === 'all' || inspection.status === filterStatus;
        const matchesDate = !filterDate || inspection.date === filterDate;
        return matchesSearch && matchesStatus && matchesDate;
    });

    const getPhotoCount = (inspection) => {
        return inspection.photos?.length || 0;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <Loader2 className="w-8 h-8 text-[#1B7EB5] animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="inspections-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Daily Inspections</h1>
                    <p className="text-zinc-500">{inspections.length} total inspections</p>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative sm:flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                        placeholder="Search by driver or vehicle..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 bg-white border-slate-300 text-slate-900"
                        data-testid="inspection-search-input"
                    />
                </div>
                <Input
                    type="date"
                    value={filterDate}
                    onChange={(e) => setFilterDate(e.target.value)}
                    className="sm:w-40 bg-white border-slate-300 text-slate-900"
                    data-testid="inspection-filter-date"
                />
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="sm:w-40 bg-white border-slate-300 text-slate-900" data-testid="inspection-filter-status">
                        <Filter className="w-4 h-4 mr-2" />
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="flagged">Flagged</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Table */}
            <div className="fleet-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Driver</th>
                                <th>Vehicle</th>
                                <th>Odometer</th>
                                <th>Photos</th>
                                <th>Status</th>
                                <th className="w-20">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredInspections.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="text-center py-8">
                                        <div className="empty-state">
                                            <ClipboardCheck className="empty-state-icon mx-auto" />
                                            <p>No inspections found</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                filteredInspections.map((inspection) => (
                                    <tr key={inspection.id} className="table-row-hover" data-testid={`inspection-row-${inspection.id}`}>
                                        <td>
                                            <div className="flex items-center gap-2">
                                                <Calendar className="w-4 h-4 text-[#1B7EB5]" />
                                                <span>{new Date(inspection.date).toLocaleDateString()}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="flex items-center gap-2">
                                                <User className="w-4 h-4 text-zinc-500" />
                                                <span className="text-white">{inspection.driver_name}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="flex items-center gap-2">
                                                <Truck className="w-4 h-4 text-zinc-500" />
                                                <span>{inspection.vehicle_info}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="flex items-center gap-2">
                                                <Gauge className="w-4 h-4 text-green-500" />
                                                <span>{inspection.odometer_reading?.toLocaleString()} km</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="flex items-center gap-2">
                                                <Camera className={`w-4 h-4 ${getPhotoCount(inspection) === 6 ? 'text-green-500' : 'text-[#1B7EB5]'}`} />
                                                <span className={getPhotoCount(inspection) === 6 ? 'text-green-500' : 'text-[#1B7EB5]'}>
                                                    {getPhotoCount(inspection)}/6
                                                </span>
                                            </div>
                                        </td>
                                        <td><StatusBadge status={inspection.status} /></td>
                                        <td>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleViewInspection(inspection)}
                                                className="text-slate-500 hover:text-slate-700"
                                                data-testid={`view-inspection-${inspection.id}`}
                                            >
                                                <Eye className="w-4 h-4 mr-1" />
                                                View
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* View Inspection Dialog */}
            <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
                <DialogContent className="bg-zinc-900 border-zinc-800 max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <ClipboardCheck className="w-5 h-5 text-[#1B7EB5]" />
                            {t.inspectionDetails}
                        </DialogTitle>
                    </DialogHeader>
                    
                    {selectedInspection && (
                        <div className="space-y-6 mt-4">
                            {/* Info Grid */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-3">
                                    <p className="text-xs text-zinc-400 uppercase">{t.date}</p>
                                    <p className="text-white font-medium">{new Date(selectedInspection.date).toLocaleDateString(language === 'el' ? 'el-GR' : 'en-US')}</p>
                                </div>
                                <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-3">
                                    <p className="text-xs text-zinc-400 uppercase">{t.driver}</p>
                                    <p className="text-white font-medium">{selectedInspection.driver_name}</p>
                                </div>
                                <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-3">
                                    <p className="text-xs text-zinc-400 uppercase">{t.vehicle}</p>
                                    <p className="text-white font-medium text-sm">{selectedInspection.vehicle_info}</p>
                                </div>
                                <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-3">
                                    <p className="text-xs text-zinc-400 uppercase">{t.odometer}</p>
                                    <p className="text-white font-medium">{selectedInspection.odometer_reading?.toLocaleString()} km</p>
                                </div>
                            </div>

                            {/* Notes */}
                            {selectedInspection.notes && (
                                <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-4">
                                    <p className="text-xs text-zinc-400 uppercase mb-2">{t.notes}</p>
                                    <p className="text-zinc-300">{selectedInspection.notes}</p>
                                </div>
                            )}

                            {/* Photos */}
                            <div>
                                <p className="text-sm font-medium text-zinc-300 mb-3">{t.inspectionPhotos}</p>
                                <PhotoGrid inspection={selectedInspection} />
                            </div>

                            {/* Actions */}
                            {canManage() && (
                                <div className="flex flex-wrap justify-end gap-3 pt-4 border-t border-zinc-800">
                                    {/* Report Issue Button - Always visible for managers */}
                                    <Button
                                        variant="outline"
                                        onClick={handleOpenIssueReport}
                                        className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                                        data-testid="report-issue-btn"
                                    >
                                        <FileWarning className="w-4 h-4 mr-2" />
                                        {t.reportIssue}
                                    </Button>
                                    
                                    {selectedInspection.status === 'pending' && (
                                        <>
                                            <Button
                                                variant="outline"
                                                onClick={() => handleUpdateStatus(selectedInspection.id, 'flagged')}
                                                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                                                data-testid="flag-inspection-btn"
                                            >
                                                <AlertTriangle className="w-4 h-4 mr-2" />
                                                {t.flagIssue}
                                            </Button>
                                            <Button
                                                onClick={() => handleUpdateStatus(selectedInspection.id, 'approved')}
                                                className="bg-green-600 hover:bg-green-500 text-white"
                                                data-testid="approve-inspection-btn"
                                            >
                                                <Check className="w-4 h-4 mr-2" />
                                                {t.approve}
                                            </Button>
                                        </>
                                    )}
                                    
                                    {selectedInspection.status !== 'pending' && (
                                        <StatusBadge status={selectedInspection.status} />
                                    )}
                                </div>
                            )}
                            
                            {!canManage() && selectedInspection.status !== 'pending' && (
                                <div className="flex justify-end pt-4 border-t border-zinc-800">
                                    <StatusBadge status={selectedInspection.status} />
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Issue Report Modal */}
            <Dialog open={issueReportOpen} onOpenChange={(open) => !submittingIssue && setIssueReportOpen(open)}>
                <DialogContent className="bg-white border-slate-200 max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-slate-800 flex items-center gap-2">
                            <FileWarning className="w-5 h-5 text-orange-500" />
                            {t.issueReport}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4 mt-4">
                        {/* Inspection Info */}
                        {selectedInspection && (
                            <div className="bg-slate-50 rounded-lg p-3 text-sm">
                                <p className="text-slate-600">
                                    <span className="font-medium">{t.vehicle}:</span> {selectedInspection.vehicle_info}
                                </p>
                                <p className="text-slate-600">
                                    <span className="font-medium">{t.driver}:</span> {selectedInspection.driver_name}
                                </p>
                                <p className="text-slate-600">
                                    <span className="font-medium">{t.date}:</span> {new Date(selectedInspection.date).toLocaleDateString(language === 'el' ? 'el-GR' : 'en-US')}
                                </p>
                            </div>
                        )}
                        
                        {/* Photo Selection */}
                        <div className="space-y-2">
                            <Label className="text-slate-700">{t.selectPhoto}</Label>
                            <Select 
                                value={issueReport.photo_type} 
                                onValueChange={(v) => setIssueReport({...issueReport, photo_type: v})}
                            >
                                <SelectTrigger className="bg-white border-slate-300">
                                    <SelectValue placeholder={t.allPhotos} />
                                </SelectTrigger>
                                <SelectContent className="bg-white border-slate-200">
                                    <SelectItem value="all">{t.allPhotos}</SelectItem>
                                    {Object.entries(language === 'el' ? PHOTO_LABELS_EL : PHOTO_LABELS).map(([key, label]) => (
                                        <SelectItem key={key} value={key}>{label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        
                        {/* Severity */}
                        <div className="space-y-2">
                            <Label className="text-slate-700">{t.severity}</Label>
                            <Select 
                                value={issueReport.severity} 
                                onValueChange={(v) => setIssueReport({...issueReport, severity: v})}
                            >
                                <SelectTrigger className="bg-white border-slate-300">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-white border-slate-200">
                                    <SelectItem value="low">
                                        <span className="flex items-center gap-2">
                                            <span className="w-2 h-2 rounded-full bg-yellow-500" />
                                            {t.low}
                                        </span>
                                    </SelectItem>
                                    <SelectItem value="medium">
                                        <span className="flex items-center gap-2">
                                            <span className="w-2 h-2 rounded-full bg-orange-500" />
                                            {t.medium}
                                        </span>
                                    </SelectItem>
                                    <SelectItem value="high">
                                        <span className="flex items-center gap-2">
                                            <span className="w-2 h-2 rounded-full bg-red-500" />
                                            {t.high}
                                        </span>
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        
                        {/* Description */}
                        <div className="space-y-2">
                            <Label className="text-slate-700">{t.description}</Label>
                            <Textarea
                                value={issueReport.description}
                                onChange={(e) => setIssueReport({...issueReport, description: e.target.value})}
                                placeholder={t.descriptionPlaceholder}
                                className="bg-white border-slate-300 min-h-[100px]"
                            />
                        </div>
                        
                        {/* Buttons */}
                        <div className="flex gap-3 pt-2">
                            <Button
                                variant="outline"
                                onClick={() => setIssueReportOpen(false)}
                                disabled={submittingIssue}
                                className="flex-1 border-slate-300"
                            >
                                {t.cancel}
                            </Button>
                            <Button
                                onClick={handleSubmitIssueReport}
                                disabled={submittingIssue || !issueReport.description.trim()}
                                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                            >
                                {submittingIssue ? (
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                ) : (
                                    <Send className="w-4 h-4 mr-2" />
                                )}
                                {t.submit}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
